/**
 * Renders the local wall-clock time of a `Date` as `HH:mm`.
 *
 * Local, not UTC: a `dateTime` is an INSTANT, and a reader expects to see an
 * instant in their own zone. The model still carries UTC — the conversion
 * belongs to the display edge, because only the browser knows the viewer's
 * zone. The server deliberately applies no timezone at all.
 *
 * Minutes are the finest unit on purpose: seconds add noise to a table column
 * without adding information a reader acts on.
 *
 * @param date A `Date` built from the model's UTC instant.
 * @returns The local time formatted as `HH:mm`.
 *
 * @example
 * localTimeHhMm(new Date('2026-08-01T14:23:45Z')); // → '16:23' in UTC+2
 */
export function localTimeHhMm(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Renders the local wall-clock time of a `Date` as `HH:mm:ss`.
 *
 * The seconds-bearing sibling of {@link localTimeHhMm}, for the `<input
 * type="time" step="1">` of `au-datetime-picker`, whose contract captures
 * seconds. Display surfaces use the coarser `HH:mm`.
 *
 * @param date A `Date` built from the model's UTC instant.
 * @returns The local time formatted as `HH:mm:ss`.
 *
 * @example
 * localTimeHhMmSs(new Date('2026-08-01T14:23:45Z')); // → '16:23:45' in UTC+2
 */
export function localTimeHhMmSs(date: Date): string {
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${localTimeHhMm(date)}:${seconds}`;
}
